import { Info } from "lucide-react";
import { fmtMoney } from "@/lib/api";

function ConsideracionesInfoBanner({ ventana, nota }) {
  const defaultLines = [
    `La confronta usa los XML de su instalación; el portal SAT puede tardar hasta ${ventana} días hábiles (sin fines de semana ni festivos oficiales en México) en reflejar complementos, cancelaciones u otros movimientos.`,
    "Lista de referencia si los visores no coinciden; no modifica los totales de diferencia.",
  ];
  const lines = nota
    ? nota.split("\n").map((s) => s.trim()).filter(Boolean)
    : defaultLines;

  return (
    <div
      className="rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 flex gap-3 shrink-0"
      data-testid="confronta-consideraciones-info"
    >
      <Info className="w-5 h-5 shrink-0 mt-0.5" aria-hidden />
      <div className="min-w-0 space-y-1.5">
        <p className="font-semibold m-0">Desfase habitual del portal SAT</p>
        {lines.map((line) => (
          <p key={line} className="m-0 text-xs leading-snug">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

/**
 * Pestaña informativa: CFDI del periodo aún dentro de la ventana habitual de actualización del portal SAT.
 */
export default function ConfrontaConsideracionesTab({
  consideraciones = null,
  onOpenSatVisor,
  activeClientId,
}) {
  const payload = consideraciones && typeof consideraciones === "object" ? consideraciones : {};
  const items = Array.isArray(payload.items) ? payload.items : [];
  const ventana = Number(payload.ventana_dias_habiles) || 5;
  const nota = payload.nota || "";

  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500" data-testid="confronta-consideraciones-tab">
        <p className="m-0 max-w-2xl mx-auto leading-relaxed">
          Ningún XML o CFDI del período requiere revisión por desfase del visor SAT en este momento.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="confronta-consideraciones-tab">
      <p className="text-xs text-slate-600 m-0 leading-relaxed">
        Se detectaron <strong>{items.length}</strong> comprobante{items.length === 1 ? "" : "s"} del período
        dentro de la ventana de actualización del visor SAT ({ventana} días hábiles). Conviene revisarlos si
        aún no aparecen en el portal web del SAT; la confronta sigue usando los XML ya cargados en su instalación.
      </p>
      <ConsideracionesInfoBanner ventana={ventana} nota={nota} />
      <div className="rounded-sm border border-slate-200 overflow-x-auto w-full">
        <table className="w-full text-xs table-fixed min-w-[1080px]">
          <colgroup>
            <col style={{ width: "7%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "21%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "31%" }} />
          </colgroup>
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Fecha</th>
              <th className="text-left px-3 py-2 font-semibold">Folio</th>
              <th className="text-right px-3 py-2 font-semibold">Monto</th>
              <th className="text-left px-3 py-2 font-semibold">UUID</th>
              <th className="text-left px-3 py-2 font-semibold pr-4">Tipo</th>
              <th className="text-left px-3 py-2 font-semibold pl-2 leading-tight">
                <span className="block">Límite</span>
                <span className="block font-normal text-[10px]">estimado SAT</span>
              </th>
              <th className="text-left px-3 py-2 font-semibold">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.uuid} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                  {row.fecha || "—"}
                  {row.fecha_pago_periodo && row.fecha_pago_periodo !== row.fecha ? (
                    <span className="block text-[10px] text-slate-500 font-normal">
                      Pago: {row.fecha_pago_periodo}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 font-mono whitespace-nowrap">
                  {row.serie_folio || "—"}
                  {row.folio_complemento ? (
                    <span className="block text-[10px] text-slate-500 font-normal font-sans">
                      Comp.: {row.folio_complemento}
                    </span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                  {Number.isFinite(Number(row.monto)) ? fmtMoney(row.monto) : "—"}
                </td>
                <td className="px-3 py-2 font-mono text-[10px] whitespace-nowrap overflow-hidden text-ellipsis" title={row.uuid}>
                  {onOpenSatVisor && activeClientId ? (
                    <button
                      type="button"
                      className="text-sky-700 hover:underline text-left max-w-full truncate"
                      onClick={() => onOpenSatVisor(row.uuid, row.fecha)}
                      title="Abrir en visor SAT"
                    >
                      {row.uuid}
                    </button>
                  ) : (
                    row.uuid || "—"
                  )}
                </td>
                <td className="px-3 py-2 pr-4 leading-snug break-words align-top overflow-hidden">
                  <span className="block">{row.tipo_label || "—"}</span>
                  {row.motivo ? (
                    <span className="block text-[10px] text-slate-500 font-normal mt-1 leading-snug sm:hidden">
                      {row.motivo}
                    </span>
                  ) : null}
                </td>
                <td className="px-2 py-2 tabular-nums whitespace-nowrap align-top">
                  {row.fecha_limite_portal_sat || "—"}
                </td>
                <td className="px-3 py-2 text-slate-600 leading-snug break-words hidden sm:table-cell">
                  {row.motivo || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
